/* eslint-disable global-require */
/* eslint-disable consistent-return */
import React, {
  createContext,
  ReactNode,
  useReducer,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  HopeGlobalContext,
  SET_CROSS_PATIENT_HOPE,
  SET_HOPE_DETAIL,
} from '@mummssoftware/global-context';
import {
  removeExtraData,
  buildHopePatientDetailPayload,
  filterHopeListByAction,
  download,
  approve,
} from '@mummssoftware/utils/hope';
import { formatPostDate } from '@mummssoftware/utils/formatters';
import { hopeCMSAcceptanceStatuses } from '@mummssoftware/utils';
import JSZip from 'jszip';
import { Context } from '../../context';
import {
  SET_HOPE_DOC_DATA,
  SET_HOPE_ERROR,
  SET_SHOW_LOADER,
  SET_SHOW_DETAIL,
  SET_BATCH_MODE,
  SET_SELECTED_ROWS,
  HopeBatchMode,
} from './constants';
import { zipHOPEFiles, getBatchHopeData, updateHopeRows } from './utils';

type HopeContextProviderProps = {
  children: ReactNode;
  gridApiRef: any;
};

type initFunction = () => undefined;

const initFunc = () => undefined;

type HopeContext = {
  patientHopeDocData: any | null;
  getPatientHopeReviewItems: any;
  DownloadUnsentXML: any;
  getPatientHopeDetails: any;
  showDetailRow: mumms.HopeEvent | null;
  clearHopeData: any;
  showLoader: boolean;
  hopeError: string | null;
  updateHopeWidgetStatus: any;
  approveAndDownloadXML: any;
  batchMode: HopeBatchMode | null;
  filteredHopeList: mumms.HopeEvent[] | null;
  batchDownloadXML: any;
  batchHopeActionUpdate: any;
  selectedRows: mumms.HopeEvent[] | null;
  dispatch: any;
};

type HopeContextState = {
  patientHopeDocData: any | null;
  getPatientHopeReviewItems: any;
  DownloadUnsentXML: any;
  getPatientHopeDetails: any;
  showDetailRow: mumms.HopeEvent | null;
  clearHopeData: any;
  approveAndDownloadXML: any;
  showLoader: boolean;
  hopeError: string | null;
  updateHopeWidgetStatus: any;
  batchMode: HopeBatchMode | null;
  batchDownloadXML: any;
  batchHopeActionUpdate: any;
  selectedRows: mumms.HopeEvent[] | null;
  dispatch: any;
};

const initialState = {
  patientHopeDocData: null,
  getPatientHopeReviewItems: initFunc,
  DownloadUnsentXML: initFunc,
  getPatientHopeDetails: initFunc,
  showDetailRow: null,
  clearHopeData: initFunc,
  approveAndDownloadXML: initFunc,
  showLoader: false,
  hopeError: null,
  updateHopeWidgetStatus: initFunc,
  batchMode: null,
  batchDownloadXML: initFunc,
  batchHopeActionUpdate: initFunc,
  selectedRows: null,
  dispatch: initFunc,
};

const initHOPEContext = {
  patientHopeDocData: null,
  getPatientHopeReviewItems: initFunc,
  DownloadUnsentXML: initFunc,
  getPatientHopeDetails: initFunc,
  showDetailRow: null,
  clearHopeData: initFunc,
  approveAndDownloadXML: initFunc,
  showLoader: false,
  hopeError: null,
  updateHopeWidgetStatus: initFunc,
  batchMode: null,
  filteredHopeList: null,
  batchDownloadXML: initFunc,
  batchHopeActionUpdate: initFunc,
  selectedRows: null,
  dispatch: initFunc,
};

export const HopeContextReducer = (
  state: HopeContextState,
  action: { type: string; payload: mumms.ComponentName | any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_HOPE_ERROR:
      newState.hopeError = action.payload;
      break;
    case SET_SHOW_DETAIL:
      newState.showDetailRow = action.payload;
      break;
    case SET_HOPE_DOC_DATA:
      newState.patientHopeDocData = action.payload;
      break;
    case SET_SHOW_LOADER:
      newState.showLoader = action.payload;
      break;
    case SET_BATCH_MODE:
      newState.batchMode = action.payload;
      break;
    case SET_SELECTED_ROWS:
      newState.selectedRows = action.payload;
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const HopeContext = createContext<HopeContext>(
  initHOPEContext as HopeContext,
);
export const HopeContextProvider = ({
  children,
  gridApiRef,
}: HopeContextProviderProps) => {
  const [
    {
      patientHopeDocData,
      showLoader,
      showDetailRow,
      hopeError,
      batchMode,
      selectedRows,
    },
    dispatch,
  ] = useReducer(HopeContextReducer, initialState);

  const { agency, workflow, siteId, patient } = useContext(Context);
  const {
    dispatch: dispatchHopeGlobal,
    globalCrossPatientHopeData,
    patientHopeDetail,
  } = useContext(HopeGlobalContext);

  const filteredHopeList = useMemo(() => {
    if (!batchMode || !globalCrossPatientHopeData) return null;
    return filterHopeListByAction(globalCrossPatientHopeData, batchMode);
  }, [batchMode, globalCrossPatientHopeData]);

  const getGlobalCrossPatientHopeData = async () => {
    const hopeData = await workflow?.getCrossPatientHope(agency, siteId || '');
    if (!(hopeData instanceof Response) && !!hopeData) {
      dispatchHopeGlobal &&
        dispatchHopeGlobal({
          type: SET_CROSS_PATIENT_HOPE,
          payload: removeExtraData(hopeData).map((dto: any) => ({
            ...dto,
            summary: true,
          })),
        });
      dispatch({
        type: SET_SELECTED_ROWS,
        payload: null,
      });
      dispatch({ type: SET_SHOW_LOADER, payload: false });
    } else {
      dispatch({
        type: SET_HOPE_ERROR,
        payload: `${hopeData.status} - Error fetching cross-patient details`,
      });
    }
    dispatch({ type: SET_SHOW_LOADER, payload: false });
    return hopeData;
  };

  const refreshCrossPatientHopeSummary = () => {
    if (patient) {
      return;
    }
    dispatchHopeGlobal({ type: SET_HOPE_DETAIL, payload: null });
    dispatchHopeGlobal({
      type: SET_CROSS_PATIENT_HOPE,
      payload: null,
    });
  };

  const clearHopeData = ({
    refreshSummary = false,
  }: { refreshSummary?: boolean } = {}) => {
    dispatch({
      type: SET_HOPE_DOC_DATA,
      payload: null,
    });
    dispatch({ type: SET_SHOW_LOADER, payload: false });
    dispatch({ type: SET_SHOW_DETAIL, payload: null });
    dispatchHopeGlobal({ type: SET_HOPE_DETAIL, payload: null });
    if (refreshSummary) {
      refreshCrossPatientHopeSummary();
    }
  };

  useEffect(() => {
    if (!globalCrossPatientHopeData && !patient) {
      getGlobalCrossPatientHopeData();
    }
  }, [globalCrossPatientHopeData, patient]);

  useEffect(() => {
    if (patientHopeDetail) return;

    if (patient) {
      // patient dashboard
      getPatientHopeDetails(`${patient.id}`, `${patient.episodes.length - 1}`);
    } else if (showDetailRow) {
      // coss-patient detail open
      getPatientHopeDetails(
        `${showDetailRow.patientId}`,
        `${showDetailRow.episodenum}`,
      );
    }
  }, [patient, patientHopeDetail]);

  const getPatientHopeReviewItems = async (
    patientNumber: string,
    episodeNumber: string,
    type: string,
  ) => {
    const hopeData = await workflow?.getHopeReviewItems(
      patientNumber,
      episodeNumber,
      type,
      agency,
      siteId || '',
    );
    if (!(hopeData instanceof Response)) {
      dispatch({
        type: SET_HOPE_DOC_DATA,
        payload: hopeData,
      });
      dispatch({ type: SET_SHOW_LOADER, payload: false });
    } else {
      dispatch({
        type: SET_HOPE_ERROR,
        payload: `${hopeData.status} - Error fetching ${type} review document fo patient number ${patientNumber}`,
      });
      clearHopeData();
    }
    return hopeData;
  };

  const getPatientHopeDetails = async (
    patient_id: string,
    episodeNumber?: string,
  ) => {
    const hopeDetailData = await workflow?.getHopePatientDetails(
      patient_id,
      episodeNumber || '0',
      agency,
      siteId || '',
    );
    if (!(hopeDetailData instanceof Response)) {
      dispatchHopeGlobal({
        type: SET_HOPE_DETAIL,
        payload: buildHopePatientDetailPayload(hopeDetailData),
      });
      dispatch({ type: SET_SHOW_LOADER, payload: false });
    } else {
      dispatch({
        type: SET_HOPE_ERROR,
        payload: `${hopeDetailData.status} - Error fetching details for patient id ${patient_id}`,
      });
      clearHopeData();
    }
  };

  const DownloadUnsentXML = async (
    patientId: string,
    episodeNum: string,
    milestone: string,
  ) => {
    const xmlResponse = await workflow.getHopePatientXML(
      patientId,
      episodeNum,
      milestone,
      agency || '',
      siteId || '',
    );
    const zip = new JSZip();
    const HISZipFolder = zip.folder('hospice_data');

    if (xmlResponse instanceof Response) {
      dispatch({
        type: SET_HOPE_ERROR,
        payload: `${xmlResponse.status} - Error fetching ${milestone} xml for patient id ${patientId}`,
      });
      clearHopeData();
    } else if (xmlResponse.xml && HISZipFolder) {
      zipHOPEFiles(xmlResponse, HISZipFolder);
      dispatch({ type: SET_SHOW_LOADER, payload: false });
    }
    return xmlResponse;
  };

  const updateHopeWidgetStatus = async (
    patientId: string,
    episodeId: string,
    milestone: string,
    status: string,
  ) => {
    const postMilestone = milestone.split('-')[0];

    const action = (hopeCMSAcceptanceStatuses as any)[milestone][status];
    const update = {
      patientid: patientId,
      episodeid: episodeId,
      visitid: '',
      milestone: postMilestone,
      effectiveDateUtc: formatPostDate(new Date()),
      action,
    };
    const statusResponse = await workflow.postHopeStatusUpdate(
      update as any,
      agency || '',
      siteId || '',
    );
    if (statusResponse instanceof Response) {
      dispatch({
        type: SET_HOPE_ERROR,
        payload: `${statusResponse.status} - Error updating patient_id: ${patientId} status: ${status} for milestone ${milestone}`,
      });
      dispatch({ type: SET_SHOW_LOADER, payload: false });
    }
    //! commented out because of backend bug.

    // if (!!globalCrossPatientHopeData) {
    // const updatedRows = updateHopeRows(globalCrossPatientHopeData, [
    //   statusResponse,
    // ]);
    // dispatchHopeGlobal({
    //   type: SET_CROSS_PATIENT_HOPE,
    //   payload: updatedRows,
    // });
    dispatchHopeGlobal({ type: SET_HOPE_DETAIL, payload: null });
    dispatch({ type: SET_SHOW_LOADER, payload: false });
    refreshCrossPatientHopeSummary();
  };

  const approveAndDownloadXML = async (
    patientId: string,
    episodeId: string,
    milestone: string,
  ) => {
    dispatch({ type: SET_SHOW_LOADER, payload: true });
    const postMilestone = milestone.split('-')[0];
    const action = (hopeCMSAcceptanceStatuses as any)[milestone][approve];
    const update = {
      patientid: patientId,
      episodeid: episodeId,
      visitid: '',
      milestone: postMilestone,
      effectiveDateUtc: formatPostDate(new Date()),
      action,
    };
    const statusResponse = await workflow.postHopeStatusUpdate(
      update as any,
      agency || '',
      siteId || '',
    );
    if (statusResponse instanceof Response) {
      dispatch({
        type: SET_HOPE_ERROR,
        payload: `${statusResponse.status} - Error approving patient_id: ${patientId} for milestone ${milestone}`,
      });
      dispatch({ type: SET_SHOW_LOADER, payload: false });
      return;
    }
    const xmlResponse = await DownloadUnsentXML(
      patientId,
      episodeId,
      milestone,
    );
    if (xmlResponse instanceof Response) {
      return;
    }
    const downloadAction = (hopeCMSAcceptanceStatuses as any)[milestone][
      download
    ];
    const downloadUpdate = {
      patientid: patientId,
      episodeid: episodeId,
      visitid: '',
      milestone: postMilestone,
      effectiveDateUtc: formatPostDate(new Date()),
      action: downloadAction,
    };
    const downloadStatusResponse = await workflow.postHopeStatusUpdate(
      downloadUpdate as any,
      agency || '',
      siteId || '',
    );
    if (downloadStatusResponse instanceof Response) {
      dispatch({
        type: SET_HOPE_ERROR,
        payload: `${downloadStatusResponse.status} - Error marking patient_id: ${patientId} as downloaded for milestone ${milestone}`,
      });
      dispatch({ type: SET_SHOW_LOADER, payload: false });
      return;
    }
    dispatchHopeGlobal({ type: SET_HOPE_DETAIL, payload: null });
    dispatch({ type: SET_SHOW_LOADER, payload: false });
    refreshCrossPatientHopeSummary();
  };

  const batchDownloadXML = async (hopeList: any) => {
    dispatch({ type: SET_SHOW_LOADER, payload: true });
    const data = getBatchHopeData(hopeList, download);
    const xmlDataList = await workflow.getBatchHopePatientXML(
      data,
      agency,
      siteId || '',
    );
    if (!(xmlDataList instanceof Response)) {
      const zip = new JSZip();
      const HISZipFolder = zip.folder('hospice_data');
      zipHOPEFiles(xmlDataList, HISZipFolder);
      batchHopeActionUpdate(hopeList, download);
    } else {
      dispatch({
        type: SET_HOPE_ERROR,
        payload: `${xmlDataList.status} - Error generating batch xml `,
      });
      dispatch({ type: SET_SHOW_LOADER, payload: false });
    }
  };

  const batchHopeActionUpdate = async (hopeList: any, action: string) => {
    dispatch({ type: SET_SHOW_LOADER, payload: true });
    const data = getBatchHopeData(hopeList, action);
    const statusData = await workflow.getBatchHopeStatusUpdate(
      data,
      agency,
      siteId || '',
    );
    if (!(statusData instanceof Response)) {
      refreshCrossPatientHopeSummary();
    } else {
      dispatch({
        type: SET_HOPE_ERROR,
        payload: `${statusData.status} - Error batch updating for xml `,
      });
      dispatch({ type: SET_SHOW_LOADER, payload: false });
    }
  };

  return (
    <HopeContext.Provider
      value={{
        patientHopeDocData,
        getPatientHopeReviewItems,
        DownloadUnsentXML,
        getPatientHopeDetails,
        showDetailRow,
        clearHopeData,
        showLoader,
        hopeError,
        updateHopeWidgetStatus,
        approveAndDownloadXML,
        batchMode,
        filteredHopeList,
        batchDownloadXML,
        batchHopeActionUpdate,
        selectedRows,
        dispatch,
      }}
    >
      {children}
    </HopeContext.Provider>
  );
};

export default HopeContextProvider;
